import { Component, ChangeDetectionStrategy, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Client, ClientAllowedScope, ClientAllowedScopeForCreation, ClientForCreation, ClientForUpdate, ClientRedirectUriForCreation, ClientPostLogoutRedirectUriForCreation, ClientAllowedGrantTypeForCreation } from 'reg-hub-common';
import { ClientRepositoryService } from '../../../services/clients/client-repository.service';
import { Clipboard } from '@angular/cdk/clipboard'; // Import Clipboard service
import { ClientOnCreation } from 'reg-hub-common';

@Component({
  selector: 'app-client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientFormComponent {

  clientForm: FormGroup;
  clientAdded: Boolean = false;
  clientSecret: string = "";
  clientName: string = "";
  client: Client | null = null;

  constructor(
    private fb: FormBuilder,
    private clientService: ClientRepositoryService,
    private dialogRef: MatDialogRef<ClientFormComponent>,
    private clipboard: Clipboard,
    @Inject(MAT_DIALOG_DATA) protected data?: { clientId?: string; }
  ) {
    this.clientForm = this.fb.group({
      clientID: ['', Validators.required],
      requireClientSecret: [null],
      allowedGrantTypesCsv: [null],
      requirePkce: [null],
      allowAccessTokensViaBrowser: [null],
      allowPlainTextPkce: [null],
      redirectUrisCsv: [null],
      postLogoutRedirectUrisCsv: [null],
      allowedScopesCsv: [null],
      allowOfflineAccess: [null],
      requireConsent: [null],
      accessTokenLifetime: [null]
    });
  }

  ngOnInit(): void {
    if (this.data?.clientId) {
      this.clientService.getClient(this.data.clientId).subscribe(client => {
        this.client = client;
        this.clientForm.get('clientID')?.disable();
        this.clientForm.patchValue({
          ...client,
          allowedScopesCsv: client.allowedScopes?.map(s => s.scope).join(', '),
          redirectUrisCsv: client.redirectUris?.map(r => r.redirectUri).join(', '),
          postLogoutRedirectUrisCsv: client.postLogoutRedirectUris?.map(r => r.postLogoutRedirectUri).join(', '),
          allowedGrantTypesCsv: client.allowedGrantTypes?.map(g => g.grantType).join(', ')
        });
      });
    } else {
      this.clientForm.patchValue({
        requireClientSecret: true,
        allowedGrantTypesCsv: "password",
        requirePkce: true,
        allowAccessTokensViaBrowser: false,
        allowPlainTextPkce: false,
        redirectUrisCsv: "",
        postLogoutRedirectUrisCsv: "",
        allowedScopesCsv: "openid, profile, RegHubAPI",
        allowOfflineAccess: true,
        requireConsent: false,
        accessTokenLifetime: 3600
      });
    }
  }

  onSubmit() {
    if (this.client) {
      // Update the existing client with the form values
      const scopes: string[] = this.clientForm.get('allowedScopesCsv')?.value.split(',');
      const redirectUris: string[] = this.clientForm.get('redirectUrisCsv')?.value.split(',');
      const postLogoutRedirectUris: string[] = this.clientForm.get('postLogoutRedirectUrisCsv')?.value.split(',');
      const allowedGrantTypes: string[] = this.clientForm.get('allowedGrantTypesCsv')?.value.split(',');

      const updatedClient: ClientForUpdate = {
        ...this.clientForm.value,
        isActive: this.client.isActive,
        allowedScopes: scopes.map(item => ({ scope: item.trim(), clientId: this.client!.id }) as ClientAllowedScopeForCreation).filter(i => i.scope.length > 0),
        redirectUris: redirectUris.map((item: string) => ({ redirectUri: item.trim(), clientId: this.client!.id }) as ClientRedirectUriForCreation).filter(i => i.redirectUri.length > 0),
        postLogoutRedirectUris: postLogoutRedirectUris.map((item: string) => ({ postLogoutRedirectUri: item.trim(), clientId: this.client!.id }) as ClientPostLogoutRedirectUriForCreation).filter(i => i.postLogoutRedirectUri.length > 0),
        allowedGrantTypes: allowedGrantTypes.map((item: string) => ({ grantType: item.trim(), clientId: this.client!.id }) as ClientAllowedGrantTypeForCreation).filter(i => i.grantType.length > 0),
      };
      this.clientService.updateClient(this.client.id, updatedClient).subscribe(
        (response) => {
          this.dialogRef.close('refresh');
        },
        (error) => {
          console.error('Error updating client:', error);
        }
      );
    } else {
      const newClient: ClientForCreation = this.clientForm.value;
      this.clientService.createClient(newClient).subscribe(
        (response: ClientOnCreation) => {
          this.clientSecret = response.clientSecret;
          this.clientName = this.clientForm.get('clientID')?.value;
          this.clientAdded = true;
        },
        (error) => {
          console.error('Error creating client:', error);
        }
      );
    }
  }

  closeDialog() {
    this.dialogRef.close('refresh');
  }

  onCancel() {
    this.dialogRef.close();
  }

  copyClientSecret() {
    this.clipboard.copy(this.clientSecret);
  }
}